import { useState, useEffect, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getMeals } from "../api/meals";
import { getPlanForDates, removeMealFromDay } from "../api/plans";
import { AuthContext } from "../context/AuthContext";

const MEAL_TYPES = ["breakfast","lunch","dinner","snacks"];

const MEAL_LABELS = {
  breakfast: "Breakfast",
  lunch:     "Lunch",
  dinner:    "Dinner",
  snacks:    "Snacks"
};

const DOW = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

function PlusIcon(){
  return(
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" style={{display:"block"}}>
      <line x1="5" y1="1" x2="5" y2="9"/>
      <line x1="1" y1="5" x2="9" y2="5"/>
    </svg>
  );
}

function toDateStr(date){
  const y = date.getFullYear();
  const m = String(date.getMonth()+1).padStart(2,"0");
  const d = String(date.getDate()).padStart(2,"0");
  return `${y}-${m}-${d}`;
}

function getMonthGrid(year, month){
  const firstDow = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month+1, 0).getDate();
  const cells = [];
  for(let i=0; i<firstDow; i++) cells.push(null);
  for(let d=1; d<=daysInMonth; d++) cells.push(new Date(year, month, d));
  while(cells.length % 7 !== 0) cells.push(null);
  return cells;
}

function getWeekDates(refDate){
  const dow = refDate.getDay();
  const sunday = new Date(refDate);
  sunday.setDate(refDate.getDate() - dow);
  return Array.from({length:7}, (_,i)=>{
    const d = new Date(sunday);
    d.setDate(sunday.getDate()+i);
    return d;
  });
}

export default function WeekPlannerPage({ meals, setMeals }){

  const { householdId } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  const [view, setView] = useState(
    ()=>localStorage.getItem("plannerView") || "month"
  );
  const [currentDate, setCurrentDate] = useState(new Date());
  const [plan, setPlan] = useState({});

  const today = toDateStr(new Date());

  // Load meals if not yet populated
  useEffect(()=>{
    if(!householdId || meals.length > 0) return;
    getMeals(householdId).then(setMeals);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[householdId]);

  // Sync current planner date range to localStorage so Grocery tab picks it up
  useEffect(()=>{
    if(view === "week"){
      const weekDates = getWeekDates(currentDate);
      localStorage.setItem("groceryStart", toDateStr(weekDates[0]));
      localStorage.setItem("groceryEnd",   toDateStr(weekDates[6]));
    } else if(view === "day"){
      const ds = toDateStr(currentDate);
      localStorage.setItem("groceryStart", ds);
      localStorage.setItem("groceryEnd",   ds);
    }
    // month view: leave grocery dates unchanged
  },[view, currentDate]);

  // Load plan data whenever the visible range changes
  const rangeKey = view === "month"
    ? `${currentDate.getFullYear()}-${currentDate.getMonth()}`
    : view === "week"
    ? toDateStr(getWeekDates(currentDate)[0])
    : toDateStr(currentDate);

  useEffect(()=>{
    if(!householdId) return;
    const dates = view === "month"
      ? getMonthGrid(currentDate.getFullYear(), currentDate.getMonth()).filter(Boolean)
      : view === "week"
      ? getWeekDates(currentDate)
      : [currentDate];
    getPlanForDates(householdId, dates.map(toDateStr)).then(data=>{
      setPlan(prev=>({...prev, ...data}));
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[householdId, rangeKey]);

  // Optimistically apply a meal added from the picker page
  useEffect(()=>{
    if(!location.state?.added) return;
    const { date, mealType, mealId } = location.state.added;
    setPlan(prev=>{
      const day = prev[date] || {};
      const current = day[mealType] || [];
      if(current.includes(mealId)) return prev;
      return { ...prev, [date]: { ...day, [mealType]: [...current, mealId] } };
    });
  },[location.state]);

  async function handleRemove(dateStr, mealType, mealId){
    await removeMealFromDay(householdId, dateStr, mealType, mealId);
    setPlan(prev=>{
      const day = prev[dateStr] || {};
      return {
        ...prev,
        [dateStr]: { ...day, [mealType]: (day[mealType]||[]).filter(id=>id!==mealId) }
      };
    });
  }

  function prev(){
    if(view === "month"){
      setCurrentDate(d=>new Date(d.getFullYear(), d.getMonth()-1, 1));
    } else if(view === "week"){
      setCurrentDate(d=>{ const n=new Date(d); n.setDate(d.getDate()-7); return n; });
    } else {
      setCurrentDate(d=>{ const n=new Date(d); n.setDate(d.getDate()-1); return n; });
    }
  }

  function next(){
    if(view === "month"){
      setCurrentDate(d=>new Date(d.getFullYear(), d.getMonth()+1, 1));
    } else if(view === "week"){
      setCurrentDate(d=>{ const n=new Date(d); n.setDate(d.getDate()+7); return n; });
    } else {
      setCurrentDate(d=>{ const n=new Date(d); n.setDate(d.getDate()+1); return n; });
    }
  }

  function changeView(v){
    localStorage.setItem("plannerView", v);
    setView(v);
  }

  function switchToWeek(date){
    setCurrentDate(date);
    changeView("week");
  }

  function switchToDay(date){
    setCurrentDate(date);
    changeView("day");
  }

  const mealMap = Object.fromEntries(meals.map(m=>[m.id, m]));

  const periodLabel = view === "month"
    ? currentDate.toLocaleDateString("en-US",{ month:"long", year:"numeric" })
    : view === "week"
    ? (()=>{
        const w = getWeekDates(currentDate);
        const s = w[0].toLocaleDateString("en-US",{ month:"short", day:"numeric" });
        const e = w[6].toLocaleDateString("en-US",{ month:"short", day:"numeric", year:"numeric" });
        return `${s} – ${e}`;
      })()
    : currentDate.toLocaleDateString("en-US",{
        weekday:"long", month:"long", day:"numeric", year:"numeric"
      });

  return(

    <div className="plannerPage">

      {/* ── HEADER ── */}
      <div className="plannerHeader">

        <div className="plannerNav">
          <button className="plannerNavBtn" onClick={prev}>‹</button>
          <span className="plannerPeriodLabel">{periodLabel}</span>
          <button className="plannerNavBtn" onClick={next}>›</button>
          <button className="plannerTodayBtn" onClick={()=>{ setCurrentDate(new Date()); }}>
            Today
          </button>
        </div>

        <div className="plannerViewToggle">
          <button
            className={`viewToggleBtn${view==="month" ? " active" : ""}`}
            onClick={()=>changeView("month")}
          >Month</button>
          <button
            className={`viewToggleBtn${view==="week" ? " active" : ""}`}
            onClick={()=>changeView("week")}
          >Week</button>
          <button
            className={`viewToggleBtn${view==="day" ? " active" : ""}`}
            onClick={()=>changeView("day")}
          >Day</button>
        </div>

      </div>

      {/* ── MONTH VIEW ── */}
      {view === "month" && (
        <div className="calMonth">

          <div className="calDowHeaders">
            {DOW.map(d=>(
              <div key={d} className="calDowHeader">{d}</div>
            ))}
          </div>

          <div className="calMonthGrid">
            {getMonthGrid(currentDate.getFullYear(), currentDate.getMonth()).map((date,i)=>{

              if(!date) return <div key={`e${i}`} className="calCell calCellEmpty" />;

              const dateStr = toDateStr(date);
              const dayPlan = plan[dateStr] || {};
              const isToday = dateStr === today;
              const allMeals = MEAL_TYPES.flatMap(t=>
                (dayPlan[t]||[]).map(id=>({ type:t, id }))
              );

              return(
                <div
                  key={dateStr}
                  className={`calCell${isToday ? " calCellToday" : ""}`}
                  onClick={()=>switchToWeek(date)}
                  title="Click to open week view"
                >
                  <div className="calCellNum">{date.getDate()}</div>
                  <div className="calCellMeals">
                    {allMeals.slice(0,3).map(({type,id})=>{
                      const meal = mealMap[id];
                      return meal ? (
                        <div key={`${type}-${id}`} className={`calMealChip calMealChip-${type}`}>
                          {meal.name}
                        </div>
                      ) : null;
                    })}
                    {allMeals.length > 3 && (
                      <div className="calMealMore">+{allMeals.length-3} more</div>
                    )}
                  </div>
                </div>
              );

            })}
          </div>

        </div>
      )}

      {/* ── WEEK VIEW ── */}
      {view === "week" && (
        <div className="calWeek">
          <div className="calWeekGrid">

            {getWeekDates(currentDate).map(date=>{

              const dateStr = toDateStr(date);
              const dayPlan = plan[dateStr] || {};
              const isToday = dateStr === today;

              return(
                <div key={dateStr} className={`calWeekDay${isToday ? " calWeekDayToday" : ""}`}>

                  <div
                    className="calWeekDayHeader"
                    onClick={()=>switchToDay(date)}
                    title="Click to open day view"
                    style={{cursor:"pointer"}}
                  >
                    <span className="calWeekDayName">
                      {date.toLocaleDateString("en-US",{weekday:"short"})}
                    </span>
                    <span className={`calWeekDayNum${isToday ? " calWeekDayNumToday" : ""}`}>
                      {date.getDate()}
                    </span>
                  </div>

                  {MEAL_TYPES.map(type=>(
                    <div key={type} className="calMealSection">

                      <div className="calMealSectionHeader">
                        <span className={`calMealTypeLabel calMealType-${type}`}>
                          {MEAL_LABELS[type]}
                        </span>
                        <button
                          className="calAddBtn"
                          onClick={()=>navigate(`/planner/pick/${dateStr}/${type}`)}
                          title={`Add ${MEAL_LABELS[type]}`}
                        ><PlusIcon /></button>
                      </div>

                      {(dayPlan[type]||[]).map(mealId=>{
                        const meal = mealMap[mealId];
                        if(!meal) return null;
                        return(
                          <div key={mealId} className="calMealItem">
                            <span className="calMealItemName">{meal.name}</span>
                            <button
                              className="calRemoveBtn"
                              onClick={()=>handleRemove(dateStr, type, mealId)}
                            >×</button>
                          </div>
                        );
                      })}

                    </div>
                  ))}

                </div>
              );

            })}

          </div>
        </div>
      )}

      {/* ── DAY VIEW ── */}
      {view === "day" && (()=>{
        const dateStr = toDateStr(currentDate);
        const dayPlan = plan[dateStr] || {};
        const isToday = dateStr === today;

        return(
          <div className="calDayView">

            <div className={`calDayDateBadge${isToday ? " calDayDateBadgeToday" : ""}`}>
              <span className="calDayDateBadgeDow">
                {currentDate.toLocaleDateString("en-US",{weekday:"long"})}
              </span>
              <span className="calDayDateBadgeNum">
                {currentDate.getDate()}
              </span>
              {isToday && <span className="calDayTodayPill">Today</span>}
            </div>

            {MEAL_TYPES.map(type=>{
              const ids = dayPlan[type] || [];
              return(
                <div key={type} className="calDaySection">
                  <div className="calDaySectionHeader">
                    <span className={`calMealTypeLabel calMealType-${type}`}>
                      {MEAL_LABELS[type]}
                    </span>
                    <button
                      className="calDayAddBtn"
                      onClick={()=>navigate(`/planner/pick/${dateStr}/${type}`)}
                    >+ Add</button>
                  </div>

                  {ids.length === 0 ? (
                    <p className="calDayEmpty">Nothing planned</p>
                  ) : (
                    ids.map(mealId=>{
                      const meal = mealMap[mealId];
                      if(!meal) return null;
                      return(
                        <div key={mealId} className="calDayMealItem">
                          <span className="calDayMealName">{meal.name}</span>
                          {meal.servings && (
                            <span className="calDayMealServes">Serves {meal.servings}</span>
                          )}
                          <button
                            className="calRemoveBtn"
                            onClick={()=>handleRemove(dateStr, type, mealId)}
                          >×</button>
                        </div>
                      );
                    })
                  )}
                </div>
              );
            })}

          </div>
        );
      })()}

    </div>

  );

}
